import * as React from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format, parseISO, isValid } from "date-fns";

export interface DatePickerProps {
  value?: string | Date;
  onChange: (date: string) => void;
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  id,
  className = "",
  placeholder = "Select date...",
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLInputElement>(null);
  const [selected, setSelected] = React.useState<Date | undefined>(() => {
    if (!value) return undefined;
    if (typeof value === "string") {
      const parsed = parseISO(value);
      return isValid(parsed) ? parsed : undefined;
    }
    return value;
  });

  React.useEffect(() => {
    if (value) {
      if (typeof value === "string") {
        const parsed = parseISO(value);
        setSelected(isValid(parsed) ? parsed : undefined);
      } else {
        setSelected(value);
      }
    } else {
      setSelected(undefined);
    }
  }, [value]);

  const handleSelect = (date?: Date) => {
    setOpen(false);
    if (date && isValid(date)) {
      setSelected(date);
      onChange(format(date, "yyyy-MM-dd"));
    }
  };

  return (
    <div className={`relative ${className}`} style={{ minWidth: 180 }}>
      <input
        id={id}
        ref={ref}
        type="text"
        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-background cursor-pointer"
        value={selected ? format(selected, "yyyy-MM-dd") : ""}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={() => {}}
        placeholder={placeholder}
        readOnly
        disabled={disabled}
        aria-label={placeholder}
      />
      {open && (
        <div
          className="absolute z-50 bg-card border rounded shadow-lg mt-1"
          style={{ minWidth: 250 }}
        >
          <DayPicker mode="single" selected={selected} onSelect={handleSelect} initialFocus />
          <button
            type="button"
            className="mt-2 mb-2 ml-2 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            onClick={() => setOpen(false)}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
